import type { CandidateConsentSummary, ConsentStatus } from './types';

// Consent the candidate actively took away — a decline (incl. the 3rd-strike block) or a
// withdrawal. An expiry is deliberately NOT here: the candidate may simply have missed the
// renewal emails, so a lapsed consent still lets the recruiter work them (product, 2026-07-13).
const BLOCKING_STATUSES: ConsentStatus[] = ['refused', 'withdrawn', 'blocked'];

export const CONSENT_BLOCKED_TOOLTIP =
  "This action needs the candidate's consent";

// Prefer the BE-computed `blocks_processing` flag; fall back to deriving from status
// (pre-rollout responses, or rows that don't carry the flag).
export const isConsentBlocked = (
  consent?: Pick<
    CandidateConsentSummary,
    'status' | 'blocks_processing' | 'anonymized'
  > | null
): boolean => {
  // Anonymized data is gone — no action can run on it, whatever the BE flag
  // says. Blocks every activity action uniformly (CORE-5142).
  if (consent?.anonymized) return true;
  if (typeof consent?.blocks_processing === 'boolean')
    return consent.blocks_processing;
  return !!consent?.status && BLOCKING_STATUSES.includes(consent.status);
};

// Same check against a candidate table row, which carries consent under `candidate_consent`.
export const isRowConsentBlocked = (selected: unknown): boolean =>
  isConsentBlocked(
    (
      selected as {
        candidate_consent?: Pick<
          CandidateConsentSummary,
          'status' | 'blocks_processing' | 'anonymized'
        >;
      }
    )?.candidate_consent
  );

interface RowAction {
  id?: string;
  isDisabled?: (selected: unknown) => boolean;
  tooltip?: unknown;
}

// Table row-actions gate through predicates rather than flags, and several carry their own
// isDisabled/tooltip (transfer limits, etc.) — so compose rather than overwrite. Falsy entries
// (permission-gated away) pass through untouched.
//
// Caveat: TableRowActions ignores `isDisabled` on a *submenu* parent (it only applies it to plain
// items), so gating a submenu row action here is a no-op — gate its subItems instead. The one
// submenu today (consent) is allow-listed, so this does not bite.
export const gateRowActionsOnConsent = <T>(
  actions: T[],
  allowedIds: string[]
): T[] =>
  actions.map((action) => {
    const row = action as RowAction;
    if (!row?.id || allowedIds.includes(row.id)) return action;
    const wasDisabled = row.isDisabled;
    const ownTooltip = row.tooltip;
    return {
      ...action,
      isDisabled: (selected: unknown) =>
        isRowConsentBlocked(selected) || !!wasDisabled?.(selected),
      tooltip: (selected: unknown) =>
        isRowConsentBlocked(selected)
          ? CONSENT_BLOCKED_TOOLTIP
          : typeof ownTooltip === 'function'
            ? ownTooltip(selected)
            : ownTooltip,
    };
  });

interface MenuAction {
  id?: string;
  disabled?: boolean;
  tooltip?: unknown;
  children?: MenuAction[];
}

// Disables every action in a MoreMenu list that a declined/withdrawn consent bars, keeping the
// allowed ids (and their sub-items) live. A no-op when the consent does not block.
export const gateActionsOnConsent = <T extends MenuAction>(
  actions: T[],
  consent:
    | Pick<
        CandidateConsentSummary,
        'status' | 'blocks_processing' | 'anonymized'
      >
    | null
    | undefined,
  allowedIds: string[]
): T[] => {
  if (!isConsentBlocked(consent)) return actions;
  const block = (action: MenuAction) => ({
    ...action,
    disabled: true,
    tooltip: CONSENT_BLOCKED_TOOLTIP,
  });
  return actions.map((action) =>
    action?.id && allowedIds.includes(action.id)
      ? action
      : {
          ...block(action),
          ...(action?.children ? { children: action.children.map(block) } : {}),
        }
  );
};
